import { importLibrary, setOptions } from '@googlemaps/js-api-loader'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import type { AddressResponse } from '~/types/global'

/** Lightweight Places autocomplete suggestion (before place details are fetched). */
export type AddressSuggestion = {
  placeId: string
  label: string
}

/** Canonical address shape used by forms and API payloads. */
export type Address = Omit<AddressResponse, 'id'> & { id?: number }

export const formatAddressMultiline = (
  v: AddressResponse | null | undefined
): string => {
  if (v == null) {
    return ''
  }
  const cityLine = [v.postcode, v.city]
    .filter((x) => x && String(x).trim())
    .join(' ')
  const lines = [v.line1, v.line2, cityLine, v.country]
    .map((x) => (typeof x === 'string' ? x.trim() : ''))
    .filter((x) => x !== '')
  return lines.join('\n')
}

// Get Google Maps API key
const getGoogleMapsApiKey = (): string => {
  return import.meta.env.VITE_GOOGLE_PLACES_API_KEY || ''
}

// Map ID required by Google Maps JS (styled / vector maps)
export const getGooglePlaceMapId = (): string => {
  return import.meta.env.VITE_GOOGLE_PLACE_MAP_ID || ''
}

setOptions({
  key: getGoogleMapsApiKey(),
  v: 'weekly'
})

// Search addresses using Google Places Autocomplete
const searchAddresses = async (query: string): Promise<AddressSuggestion[]> => {
  if (!query || query.length < 3) return []

  try {
    await importLibrary('places')
    const sessionToken = new google.maps.places.AutocompleteSessionToken()
    const service = new google.maps.places.AutocompleteService()

    // Define bounds for France (approximate center and bounds)
    const franceBounds = {
      north: 51.071002,
      south: 42.3332995,
      east: 8.1767166,
      west: -4.79034948348999
    }

    // Request predictions with France location bias (@todo better use user location)
    const response = await service.getPlacePredictions({
      input: query,
      types: ['street_address', 'locality'],
      sessionToken,
      locationBias: franceBounds
    })

    if (!response?.predictions) {
      return []
    }

    return response.predictions.map(
      (prediction: google.maps.places.AutocompletePrediction) => ({
        placeId: prediction.place_id,
        label: prediction.description
      })
    )
  } catch (error) {
    console.error('Error fetching address suggestions:', error)
    return []
  }
}

// Get place details for a selected place and map them to Address fields
export const getPlaceDetails = async (
  placeId: string
): Promise<Address | null> => {
  try {
    // Make sure the places library is loaded
    await importLibrary('places')

    // Create a session token for billing optimization
    const sessionToken = new google.maps.places.AutocompleteSessionToken()

    // Create the places service (requires a DOM element, but we're just using it for the API call)
    const tempDiv = document.createElement('div')
    const service = new google.maps.places.PlacesService(tempDiv)

    // Create a promise wrapper for the getDetails method
    const getDetailsPromise = (
      request: google.maps.places.PlaceDetailsRequest
    ): Promise<google.maps.places.PlaceResult> => {
      return new Promise((resolve, reject) => {
        service.getDetails(request, (result, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && result) {
            resolve(result)
          } else {
            reject(
              new Error(`Place details request failed with status: ${status}`)
            )
          }
        })
      })
    }

    // Get the place details
    const place = await getDetailsPromise({
      placeId,
      fields: ['address_components', 'formatted_address', 'geometry'],
      sessionToken
    })

    if (!place?.address_components) {
      return null
    }

    // Extract address components into our Address fields
    let streetNumber = ''
    let route = ''
    let postcode: string | undefined
    let city: string | undefined
    let country: string | undefined
    let countryCode: string | undefined

    for (const component of place.address_components) {
      if (component.types.includes('street_number')) {
        streetNumber = component.long_name
      }
      if (component.types.includes('route')) {
        route = component.long_name
      }
      if (component.types.includes('locality')) {
        city = component.long_name
      }
      if (component.types.includes('postal_code')) {
        postcode = component.long_name
      }
      if (component.types.includes('country')) {
        country = component.long_name
        countryCode = component.short_name
      }
    }

    const line1 = [streetNumber, route].filter(Boolean).join(' ')

    return {
      line1: line1 || undefined,
      postcode,
      city,
      country,
      countryCode,
      lat: place.geometry?.location?.lat(),
      lng: place.geometry?.location?.lng()
    }
  } catch (error) {
    console.error('Error fetching place details:', error)
    return null
  }
}

export const useGoogleAddressSearch = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false)

  // Load Google Maps API
  useEffect(() => {
    const loadGoogleMaps = async () => {
      try {
        await importLibrary('places')
        setIsGoogleLoaded(true)
      } catch (error) {
        console.error('Error loading Google Maps API:', error)
      }
    }

    loadGoogleMaps()
  }, [])

  const {
    data: suggestions = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['addressSearch', searchQuery],
    queryFn: () => searchAddresses(searchQuery),
    enabled: isGoogleLoaded && searchQuery.length >= 3,
    staleTime: 1000 * 60 * 5 // 5 minutes
  })

  return {
    suggestions,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    isGoogleLoaded
  }
}
